/**
 * @file cmp.ts
 *
 * @fileoverview Implements Usercentrics as a default. This can be overwritten
 * by other CMP implementations by putting the implementation of an alternative
 * into src/overrides/services/cmp. The alternative implementation
 * needs to implement the same functions.
 */

import { allowedCmpProviders, Consent, SERVICES } from '~/jobiqo/lib/cmp';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';
import getEssentialServicesConsents from '~/jobiqo/components/Slots/cmpEssentialServicesSlot';

const getCmpAppConfig = () => getCurrentFrontendConfig().APP || {};

/**
 * Checks the consents on the event and return an array of jobiqo Consent objects.
 * This function needs to provide a mapping between the consents on the event object
 * and the jobiqo Consent objects.
 *
 * @param event
 *  A custom event provided by a CMP provider.
 * @returns Consent[]
 *  Returns an array of jobiqo Consent objects.
 */
export const getConsents = (event): Consent[] => {
  const {
    CMP_PROVIDER,
    CMP_TCF,
    TRAFFECTIVE_PURPOSES_TO_CHECK,
  } = getCmpAppConfig();
  const consents: Consent[] = [];

  switch (CMP_PROVIDER) {
    case allowedCmpProviders.Usercentrics:
      Object.entries(event?.detail)?.forEach((service) => {
        if (Object.values(SERVICES).includes(service[0])) {
          consents.push({
            serviceID: service[0],
            status: !!service[1],
          });
        }
      });
      return consents;

    case allowedCmpProviders.OneTrust:
      Object.entries(SERVICES).forEach((service) => {
        if (event?.detail.includes(service[1])) {
          consents.push({
            serviceID: service[1],
            status: true,
          });
        } else {
          consents.push({
            serviceID: service[1],
            status: false,
          });
        }
      });
      return consents;

    case allowedCmpProviders.ConsentManager:
      Object.entries(event?.detail?.data?.vendorConsents)?.forEach(
        (service) => {
          if (Object.values(SERVICES).includes(service[0])) {
            consents.push({
              serviceID: service[0],
              status: !!service[1],
            });
          }
        },
      );
      return consents;

    case allowedCmpProviders.TrustCommander:
      Object.entries(event?.consent?.categories)?.forEach((service) => {
        if (Object.values(SERVICES).includes(service[0])) {
          consents.push({
            serviceID: service[0],
            // @ts-ignore
            status: service[1]['status'] == 'on',
          });
        }
      });
      return consents;

    case allowedCmpProviders.Traffective:
      let consentsGranted: boolean;
      // We check to how many purposes user granted consent and if it is equal or higher
      // than the value defined (TRAFFECTIVE_PURPOSES_TO_CHECK) we enable consents for all services.
      // This number can be different per project as not always all 10 purposes are used - this depends
      // on how CMP is configured on the client's side.
      // In comparison to other CMPs we are checking only purposes as with Traffective
      // we can get into situation when user reject consent for Purpose, but for specific vendor
      // Traffective still returns True. Together with client we decide to enable/display scripts/services
      // only when there is consent to all available Purposes.
      const purposesWithConsentGranted = Object.values(event).filter(
        (value) => value === true,
      ).length;
      consentsGranted = !!(
        TRAFFECTIVE_PURPOSES_TO_CHECK &&
        purposesWithConsentGranted >= TRAFFECTIVE_PURPOSES_TO_CHECK
      );

      Object.entries(SERVICES).forEach((service) => {
        consents.push({
          serviceID: service[1],
          status: consentsGranted,
        });
      });
      return consents;

    case allowedCmpProviders.Didomi:
      Object.entries(event?.vendors)?.forEach((service) => {
        if (Object.values(SERVICES).includes(service[0])) {
          consents.push({
            serviceID: service[0],
            // @ts-ignore
            status: !!service[1]['enabled'],
          });
        }
      });

      // Get consents for client specific essential services that cannot be retrieved from the CMP.
      const essentialServicesConsent = getEssentialServicesConsents();

      return [...consents, ...essentialServicesConsent];

    case allowedCmpProviders.CivicCookieControl:
      // Make sure that we always have the consent for necessary services.
      consents.push({
        serviceID: 'necessary',
        status: true,
      });
      if (CMP_TCF) {
        // If TCF is enabled, we check if the user has given consent to all Purposes.
        // In this case we set the consents for all services to true. Otherwise, we set
        // the consents for all services to false.
        let consentsGranted: boolean = false;
        const acceptedPurposes = Object.values(event).filter(
          (value) => value === true,
        ).length;
        if (acceptedPurposes > 10) {
          consentsGranted = true;
        }

        Object.entries(SERVICES).forEach((service) => {
          consents.push({
            serviceID: service[1],
            status: consentsGranted,
          });
        });
      } else {
        // Add all consents from the event to the consents array.
        Object.entries(event)?.forEach((service: any) => {
          consents.push({
            serviceID: service[0],
            status: service[1],
          });
        });
      }
      return consents;

    case allowedCmpProviders.CookieScript:
      // Categories object from CookieScript contains only categories that are
      // enabled by the user (user accepted them).
      Object.entries(event?.categories)?.forEach((service) => {
        consents.push({
          serviceID: service[1],
          status: true,
        });
      });
      return consents;

    default:
      return consents;
  }
};

/**
 * Checks if a user has given consent to a specific service.
 * Returns also true if no consent is needed (no Usercentrics ID given).
 * @param consents
 *  A list of consents that a user has accepted.
 * @param serviceID
 *  The serviceID to check the consent for.
 * @returns
 *  Returns true if no consent is needed or the consent for the service is given.
 */
export const checkUserConsentForService = (
  consents: Consent[] | undefined,
  serviceID: string,
): boolean => {
  const { CMP_ID } = getCmpAppConfig();
  const testing = process.env.testing;
  const staging = process.env.staging;
  // Returns true if consent is not needed or site is in staging or testing mode.
  if (!CMP_ID || testing || staging) {
    return true;
  }

  return !!consents?.find((consent) => serviceID === consent.serviceID)?.status;
};

/**
 * Gets the name of the consent event that gets listened to.
 *
 * @returns
 *  Returns the name of the consent event.
 */
export const consentEvent = () => {
  const { CMP_PROVIDER } = getCmpAppConfig();
  switch (CMP_PROVIDER) {
    case allowedCmpProviders.Usercentrics:
      return 'onConsentStatusChanged';

    case allowedCmpProviders.OneTrust:
      return 'consent.onetrust';

    case allowedCmpProviders.ConsentManager:
      return 'cmpEvent';

    case allowedCmpProviders.TrustCommander:
      return '';

    case allowedCmpProviders.Traffective:
      return '';

    case allowedCmpProviders.Didomi:
      return '';

    case allowedCmpProviders.CivicCookieControl:
      return '';

    case allowedCmpProviders.CookieScript:
      return '';

    default:
      return '';
  }
};
